/**
 * Prediction Accuracy Monitor
 * ---------------------------------------------------------------
 * Compares synthetic forecasts (asset_predictions) against
 * real-world platform telemetry (platform_signal_ingestion) to
 * surface forecasting accuracy, strongest/weakest predictive
 * dimensions, and confidence drift.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Target, TrendingUp, TrendingDown, Crosshair } from "lucide-react";
import { LIVE_TELEMETRY_EVENT } from "@/lib/platformIngestion";

interface MatchedRow {
  id: string;
  platform: string;
  archetype: string | null;
  format: string | null;
  escalation_path: string | null;
  predicted_virality: number;
  virality_score: number;
  prediction_accuracy: number;
  collected_at: string;
}

const PredictionAccuracyMonitor = () => {
  const [rows, setRows] = useState<MatchedRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("platform_signal_ingestion")
      .select("id, platform, archetype, format, escalation_path, predicted_virality, virality_score, prediction_accuracy, collected_at")
      .not("predicted_virality", "is", null)
      .order("collected_at", { ascending: false })
      .limit(500);
    setRows((data ?? []) as MatchedRow[]);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onIngest = () => void load();
    window.addEventListener(LIVE_TELEMETRY_EVENT, onIngest);
    return () => window.removeEventListener(LIVE_TELEMETRY_EVENT, onIngest);
  }, [load]);

  const overall = useMemo(() => {
    if (rows.length === 0) return { accuracy: 0, predicted: 0, actual: 0, delta: 0, sample: 0 };
    const acc = rows.reduce((s, r) => s + (r.prediction_accuracy || 0), 0) / rows.length;
    const predicted = rows.reduce((s, r) => s + (r.predicted_virality || 0), 0) / rows.length;
    const actual = rows.reduce((s, r) => s + (r.virality_score || 0), 0) / rows.length;
    return { accuracy: acc, predicted, actual, delta: actual - predicted, sample: rows.length };
  }, [rows]);

  const dimensions = useMemo(() => {
    const dims: Array<["archetype" | "format" | "escalation_path", string]> = [
      ["archetype", "Archetype"],
      ["format", "Format"],
      ["escalation_path", "Escalation"],
    ];
    return dims.map(([key, label]) => {
      const map = new Map<string, { acc: number[]; sample: number }>();
      rows.forEach((r) => {
        const v = (r as any)[key] as string | null;
        if (!v) return;
        if (!map.has(v)) map.set(v, { acc: [], sample: 0 });
        const x = map.get(v)!;
        x.acc.push(r.prediction_accuracy);
        x.sample += 1;
      });
      const arr = Array.from(map.entries()).map(([k, x]) => ({
        key: k,
        accuracy: x.acc.reduce((s, n) => s + n, 0) / Math.max(1, x.acc.length),
        sample: x.sample,
      })).filter((x) => x.sample >= 2);
      arr.sort((a, b) => b.accuracy - a.accuracy);
      return { label, strongest: arr.slice(0, 3), weakest: [...arr].reverse().slice(0, 3) };
    });
  }, [rows]);

  const confidenceDrift = useMemo(() => {
    if (rows.length < 10) return 0;
    const sorted = [...rows].sort((a, b) => +new Date(a.collected_at) - +new Date(b.collected_at));
    const half = Math.floor(sorted.length / 2);
    const early = sorted.slice(0, half);
    const late = sorted.slice(half);
    const a = early.reduce((s, r) => s + r.prediction_accuracy, 0) / early.length;
    const b = late.reduce((s, r) => s + r.prediction_accuracy, 0) / late.length;
    return b - a;
  }, [rows]);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="w-4 h-4 text-cyan" />
          Prediction Accuracy Monitor
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
            Predicted vs Actual · forecasting truth layer
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan" /></div>
        ) : rows.length === 0 ? (
          <div className="text-[11px] text-muted-foreground font-mono text-center py-6">
            Awaiting matched telemetry · ingest live signals to compute accuracy
          </div>
        ) : (
          <div className="space-y-5">
            {/* Top-line scoreboard */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Predicted Virality", val: Math.round(overall.predicted), tone: "text-muted-foreground" },
                { label: "Actual Virality", val: Math.round(overall.actual), tone: "text-cyan" },
                { label: "Prediction Accuracy", val: `${Math.round(overall.accuracy)}%`, tone: "text-emerald-300" },
                { label: "Confidence Drift", val: `${confidenceDrift >= 0 ? "+" : ""}${confidenceDrift.toFixed(1)}`, tone: confidenceDrift >= 0 ? "text-emerald-300" : "text-amber-300" },
              ].map((m) => (
                <div key={m.label} className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{m.label}</div>
                  <div className={`text-xl font-mono mt-1 ${m.tone}`}>{m.val}</div>
                </div>
              ))}
            </div>

            {/* Dimension analysis */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {dimensions.map((d) => (
                <div key={d.label} className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-cyan font-mono mb-2 flex items-center gap-1">
                    <Crosshair className="w-3 h-3" />{d.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-mono mb-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />Strongest
                  </div>
                  <div className="space-y-0.5 mb-2">
                    {d.strongest.length === 0 ? (
                      <div className="text-[10px] text-muted-foreground font-mono">—</div>
                    ) : d.strongest.map((s) => (
                      <div key={s.key} className="flex justify-between text-[11px] font-mono">
                        <span className="text-foreground truncate max-w-[60%]">{s.key}</span>
                        <span className="text-emerald-300">{Math.round(s.accuracy)}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-red-300/80 font-mono mb-1 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" />Weakest
                  </div>
                  <div className="space-y-0.5">
                    {d.weakest.length === 0 ? (
                      <div className="text-[10px] text-muted-foreground font-mono">—</div>
                    ) : d.weakest.map((s) => (
                      <div key={s.key} className="flex justify-between text-[11px] font-mono">
                        <span className="text-foreground truncate max-w-[60%]">{s.key}</span>
                        <span className="text-red-300">{Math.round(s.accuracy)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Most recent matches */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 font-mono mb-2">
                Recent Matches · Last 12
              </div>
              <ScrollArea className="max-h-[220px]">
                <div className="divide-y divide-glass-border">
                  {rows.slice(0, 12).map((r) => {
                    const tone = r.prediction_accuracy >= 80 ? "text-emerald-300" : r.prediction_accuracy >= 60 ? "text-amber-300" : "text-red-300";
                    return (
                      <div key={r.id} className="flex items-center justify-between gap-3 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className="text-[9px] uppercase font-mono border-cyan/30 text-cyan/80">{r.platform}</Badge>
                          <span className="text-[11px] text-foreground truncate">{r.archetype ?? "—"}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] font-mono shrink-0">
                          <span className="text-muted-foreground">P {Math.round(r.predicted_virality)}</span>
                          <span className="text-cyan">A {Math.round(r.virality_score)}</span>
                          <span className={tone}>{Math.round(r.prediction_accuracy)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PredictionAccuracyMonitor;
